import { useState } from "react";
import { Users, Check, ChevronDown, X } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { Input } from "../ui/input";
import { nameInitials } from "../../lib/nameInitials";

/**
 * Multi-select assignee popover with checkbox list.
 *
 * Renders a compact trigger showing an avatar stack (up to 3 initials)
 * plus an overflow "+N" chip. Clicking opens a searchable checkbox list.
 *
 * Consumers:
 *   - `CallLogDialog` (Sorumlu — kime atansın?)
 *   - `ActivityPlanDialog` (Sorumlu — kime atansın?)
 *
 * Empty state is explicit: "— Sorumlu atanmadı —" so users know reminders
 * will fall back to the record creator.
 */
export default function AssigneeMultiPicker({
  value = [],
  onChange,
  users = [],
  testId,
  placeholder = "Sorumlu seçin",
}) {
  const [search, setSearch] = useState("");

  const selected = new Set(value);
  const selectedUsers = users.filter((u) => selected.has(u.id));

  const toggle = (uid) => {
    if (selected.has(uid)) {
      onChange(value.filter((id) => id !== uid));
    } else {
      onChange([...value, uid]);
    }
  };

  const clearAll = (e) => {
    e.stopPropagation();
    onChange([]);
  };

  const filtered = search.trim()
    ? users.filter((u) =>
        (u.name || u.email || "")
          .toLowerCase()
          .includes(search.toLowerCase())
      )
    : users;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className="flex w-full items-center justify-between gap-2 rounded-md border border-zinc-200 bg-white px-3 py-2 text-left text-sm hover:border-zinc-300"
        >
          <span className="flex min-w-0 items-center gap-2">
            {selectedUsers.length === 0 ? (
              <>
                <Users className="h-4 w-4 shrink-0 text-zinc-400" />
                <span className="truncate text-zinc-400">{placeholder}</span>
              </>
            ) : (
              <span className="flex items-center -space-x-1.5">
                {selectedUsers.slice(0, 3).map((u) => (
                  <span
                    key={u.id}
                    title={u.name}
                    className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-violet-600 text-[10px] font-semibold text-white ring-2 ring-white"
                  >
                    {nameInitials(u.name)}
                  </span>
                ))}
                {selectedUsers.length > 3 && (
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-zinc-200 text-[10px] font-semibold text-zinc-700 ring-2 ring-white">
                    +{selectedUsers.length - 3}
                  </span>
                )}
                <span className="ml-2 truncate text-xs text-zinc-600">
                  {selectedUsers.length === 1
                    ? selectedUsers[0].name
                    : `${selectedUsers.length} kişi`}
                </span>
              </span>
            )}
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {selectedUsers.length > 0 && (
              <button
                type="button"
                onClick={clearAll}
                data-testid={testId ? `${testId}-clear` : undefined}
                className="rounded p-0.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
                title="Tümünü kaldır"
              >
                <X className="h-3 w-3" />
              </button>
            )}
            <ChevronDown className="h-3.5 w-3.5 text-zinc-400" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[280px] p-0"
        align="start"
        data-testid={testId ? `${testId}-popover` : undefined}
      >
        <div className="border-b border-zinc-100 p-2">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Kullanıcı ara..."
            className="h-8 text-xs"
            data-testid={testId ? `${testId}-search` : undefined}
          />
        </div>
        <ul className="max-h-[240px] overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-center text-[11px] text-zinc-400">
              Kullanıcı bulunamadı
            </li>
          ) : (
            filtered.map((u) => {
              const isChecked = selected.has(u.id);
              return (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={() => toggle(u.id)}
                    data-testid={testId ? `${testId}-item-${u.id}` : undefined}
                    className={`flex w-full items-center gap-2 px-3 py-1.5 text-sm transition-colors ${
                      isChecked ? "bg-violet-50 text-violet-900" : "hover:bg-zinc-50"
                    }`}
                  >
                    <span
                      className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                        isChecked
                          ? "border-violet-600 bg-violet-600 text-white"
                          : "border-zinc-300 bg-white"
                      }`}
                    >
                      {isChecked && <Check className="h-3 w-3" />}
                    </span>
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-violet-600 text-[9px] font-semibold text-white">
                      {nameInitials(u.name)}
                    </span>
                    <span className="truncate">{u.name || u.email || "—"}</span>
                  </button>
                </li>
              );
            })
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
